import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  PropertyProfileSectionConflictResponseSchema,
  PropertyProfileSectionInputSchemas,
  type PropertyProfileSectionId,
  type PropertyProfileV1,
} from '@natalan/capehost-contracts'
import { api } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { apiErrorMessage } from '@/lib/api-error'
import {
  fieldApplies,
  mergeOverTheirs,
  normalizeInputFields,
  profileSectionFields,
  sectionDef,
  storedSection,
  toInputFields,
  type InputFields,
} from '@/lib/profile'
import { ProfileFieldEditor } from './profile-field-editor'

interface ProfileSectionSheetProps {
  propertyId: string
  profile: PropertyProfileV1
  sectionId: PropertyProfileSectionId | null
  onClose: () => void
}

interface EditorProps {
  propertyId: string
  profile: PropertyProfileV1
  sectionId: PropertyProfileSectionId
  onDone: () => void
}

function issuesByPath(issues: readonly { path: readonly PropertyKey[]; message: string }[]) {
  const map = new Map<string, string>()
  for (const issue of issues) {
    const key = issue.path.map(String).join('.')
    if (!map.has(key)) map.set(key, issue.message)
  }
  return map
}

function SectionEditor({ propertyId, profile, sectionId, onDone }: EditorProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const def = sectionDef(sectionId)
  const section = storedSection(profile, sectionId)
  // The answers as opened: what "my changes" are measured against.
  const [opened, setOpened] = useState<InputFields>(() => toInputFields(def.fields, section.fields))
  const [fields, setFields] = useState<InputFields>(opened)
  // The version this form was opened on; a save against an older one is a conflict.
  const [expected, setExpected] = useState<string | null>(section.updated_at)
  const [errors, setErrors] = useState<Map<string, string>>(new Map())
  const [formError, setFormError] = useState<string | null>(null)
  // Their section, from the 409, while the conflict is open.
  const [theirs, setTheirs] = useState<InputFields | null>(null)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const [saving, setSaving] = useState(false)
  const storedFieldsOf = profileSectionFields(profile)
  // A condition naming this very section reads the answers being edited.
  const sectionFieldsOf = (id: string) => (id === sectionId ? fields : storedFieldsOf(id))
  const changedKeys = def.fields
    .map(field => field.key)
    .filter(key => JSON.stringify(fields[key]) !== JSON.stringify(opened[key]))
  const dirty = changedKeys.length > 0

  const visibleKeys = new Set(
    def.fields.filter(field => fieldApplies(field, fields, sectionFieldsOf)).map(f => f.key)
  )

  const save = async (values: InputFields) => {
    const body = normalizeInputFields(def.fields, values)
    const checked = PropertyProfileSectionInputSchemas[sectionId].safeParse(body)
    if (!checked.success) {
      const byPath = issuesByPath(checked.error.issues)
      setErrors(byPath)
      // An issue on a question not on screen (its condition does not hold) or
      // on the section itself is named here, or nothing would say why.
      const unseen = [...byPath.keys()]
        .filter(path => !visibleKeys.has(path.split('.')[0]))
        .map(path => {
          const key = path.split('.')[0]
          return def.fields.find(f => f.key === key)?.label ?? 'This section'
        })
      setFormError(
        unseen.length
          ? `Some answers need fixing before they can be saved, including ones not shown: ${[...new Set(unseen)].join(', ')}.`
          : 'Some answers need fixing before they can be saved.'
      )
      return
    }
    setErrors(new Map())
    setFormError(null)
    setSaving(true)
    try {
      await api.savePropertyProfileSection(propertyId, sectionId, {
        expected_updated_at: expected,
        fields: body,
      })
      // A refetch rather than the PUT's answer: the save can change
      // `listing_diff`, which the PUT does not return.
      void queryClient.invalidateQueries({ queryKey: ['property-profile', propertyId] })
      void queryClient.invalidateQueries({
        queryKey: ['property-profile-revisions', propertyId, sectionId],
      })
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] })
      toast({ title: `${def.title} saved`, description: 'Saved as an ops correction.' })
      onDone()
    } catch (err) {
      const conflict =
        (err as { status?: number }).status === 409
          ? PropertyProfileSectionConflictResponseSchema.safeParse((err as { body?: unknown }).body)
          : null
      if (conflict?.success) {
        const current = conflict.data.section as unknown as {
          updated_at: string | null
          fields: Record<string, unknown>
        }
        setTheirs(toInputFields(def.fields, current.fields))
        setExpected(current.updated_at)
        void queryClient.invalidateQueries({ queryKey: ['property-profile', propertyId] })
      } else {
        setFormError(apiErrorMessage(err, 'The section could not be saved. Please try again.'))
      }
    } finally {
      setSaving(false)
    }
  }

  const loadTheirs = () => {
    if (!theirs) return
    setOpened(theirs)
    setFields(theirs)
    setTheirs(null)
  }

  /**
   * Their section with only my changes laid over it, so answers I never
   * touched keep what they saved instead of reverting to what I opened.
   */
  const saveMineOverTheirs = () => {
    if (!theirs) return
    const merged = mergeOverTheirs(def.fields, opened, fields, theirs)
    setOpened(theirs)
    setFields(merged)
    setTheirs(null)
    void save(merged)
  }

  const cancel = () => {
    if (saving) return
    if (dirty && !confirmDiscard) {
      setConfirmDiscard(true)
      return
    }
    onDone()
  }

  return (
    // Every way out (Close, Escape, a click outside, Cancel) goes through the
    // same guard: not while saving, and not past unsaved edits without asking.
    <Sheet open onOpenChange={open => !open && cancel()}>
      <SheetContent
        className="sm:max-w-[560px]"
        // A click outside or Escape would drop a long correction, or a save in
        // flight, without a word: only Cancel closes, and it asks first.
        onInteractOutside={event => {
          if (saving || dirty) event.preventDefault()
        }}
      >
        <SheetHeader>
          <SheetTitle>Correct: {def.title}</SheetTitle>
          <SheetDescription>
            Saved as an ops correction. The owner&apos;s earlier answers stay in the history.
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-4">
          {theirs && (
            <div
              role="alert"
              className="space-y-2 rounded-card border border-amber/30 bg-warningBg p-3 text-sm"
            >
              <p className="font-medium text-text">
                Someone saved this section after you opened it.
              </p>
              <p className="text-textMuted">
                Load their version to start again from it, or save the answers you changed over
                theirs; the ones you did not touch keep their version.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={loadTheirs} disabled={saving}>
                  Load their version
                </Button>
                <Button size="sm" onClick={saveMineOverTheirs} disabled={saving}>
                  Save my changes over theirs
                </Button>
              </div>
            </div>
          )}
          {def.fields
            .filter(field => visibleKeys.has(field.key))
            .map(field => (
              <ProfileFieldEditor
                key={field.key}
                field={field}
                value={fields[field.key]}
                onChange={next => {
                  setConfirmDiscard(false)
                  setFields(prev => ({ ...prev, [field.key]: next }))
                }}
                disabled={saving}
                error={errors.get(field.key)}
                errorsFor={key => errors.get(`${field.key}.${key}`)}
                sectionFieldsOf={sectionFieldsOf}
              />
            ))}
        </SheetBody>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border px-6 py-3">
          {formError && (
            <p role="alert" className="mr-auto text-sm text-danger">
              {formError}
            </p>
          )}
          {confirmDiscard && <p className="mr-auto text-sm text-amber">Discard your changes?</p>}
          {confirmDiscard && (
            <Button variant="ghost" onClick={() => setConfirmDiscard(false)}>
              Keep editing
            </Button>
          )}
          <Button variant="outline" onClick={cancel} disabled={saving}>
            {confirmDiscard ? 'Discard' : 'Cancel'}
          </Button>
          {!theirs && (
            <Button onClick={() => void save(fields)} disabled={saving}>
              {saving ? 'Saving...' : 'Save correction'}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

/**
 * "Correct" (CAP-1467): one section's questions as an editable form, from
 * the same declarations the owner's portal renders, saved with
 * `PUT /admin/properties/:id/profile/sections/:sectionId` as `source: ops`.
 * Answers that were not changed keep whoever gave them.
 */
export function ProfileSectionSheet({
  propertyId,
  profile,
  sectionId,
  onClose,
}: ProfileSectionSheetProps) {
  return sectionId ? (
    <SectionEditor
      key={sectionId}
      propertyId={propertyId}
      profile={profile}
      sectionId={sectionId}
      onDone={onClose}
    />
  ) : null
}
